import {
  ActivityCategoryDto,
  ActivityTypeDto,
  CommunicationDto,
  DataPackageDto,
  PrivilegeEnum,
  ProjectActivityDto,
  SampleActivityDto,
  SampleDto,
  ShippingDto,
  StatusDto,
  TheoryActivityDto,
} from '@/api/ApiClient';
import type { Operation } from '@/stores/authStore';
import type { TreeNode } from '@/types/tree';
import { nodeIsReadOnlyForUi } from '@/lib/tree/treeNodeTypeConfigs';
import { getPrivilegeForEntity } from '@/types/tree/actions/getPrivilegeForEntity';
import {
  nodeTypeToFileParentKind,
  parentStatusAllowsFileMutation,
} from '@/types/tree/actions/fileMutationStatusPolicy';
import type { NodeType } from '@/types/tree/base';

type IsAuthorizedFn = (privilege: PrivilegeEnum | null | undefined, op: Operation) => boolean;

/** Fields from a file-parent DTO used for privilege and status policy checks. */
export type FileParentEntityData = {
  status?: string;
  canUpdateSample?: boolean;
  category?: ActivityCategoryDto;
  type?: ActivityTypeDto;
};

export type FileParentDataGetters = {
  getSampleData: (node: TreeNode) => SampleDto | null;
  getSampleActivityData: (node: TreeNode) => SampleActivityDto | null;
  getProjectActivityData: (node: TreeNode) => ProjectActivityDto | null;
  getTheoryActivityData: (node: TreeNode) => TheoryActivityDto | null;
  getShippingData: (node: TreeNode) => ShippingDto | null;
  getDataPackageData: (node: TreeNode) => DataPackageDto | null;
  getCommunicationData: (node: TreeNode) => CommunicationDto | null;
};

function activityParentFields(activity: {
  status?: string;
  category?: ActivityCategoryDto;
  type?: ActivityTypeDto;
}): FileParentEntityData {
  return {
    status: activity.status,
    category: activity.category,
    type: activity.type,
  };
}

/**
 * Extract file-parent auth fields from typed tree store getters (no manual DTO casts).
 */
export function getFileParentEntityData(node: TreeNode, getters: FileParentDataGetters): FileParentEntityData | null {
  switch (node.type) {
    case 'sample':
    case 'sampleAggregate':
    case 'sampleSnapshot':
    case 'sampleInPackage': {
      const sample = getters.getSampleData(node);
      return sample ? { status: sample.status, canUpdateSample: sample.canUpdateSample } : null;
    }
    case 'sampleActivity':
    case 'sampleActivityAggregate':
    case 'sampleActivityInPackage':
    case 'sampleActivitySnapshot': {
      const activity = getters.getSampleActivityData(node);
      return activity ? activityParentFields(activity) : null;
    }
    case 'projectActivity': {
      const activity = getters.getProjectActivityData(node);
      return activity ? activityParentFields(activity) : null;
    }
    case 'theoryActivity':
    case 'theoryActivityInPackage': {
      const activity = getters.getTheoryActivityData(node);
      return activity ? activityParentFields(activity) : null;
    }
    case 'shipping': {
      const shipping = getters.getShippingData(node);
      return shipping ? { status: shipping.status } : null;
    }
    case 'dataPackage':
    case 'simulationDataset': {
      const dataPackage = getters.getDataPackageData(node);
      return dataPackage ? { status: dataPackage.status } : null;
    }
    case 'communication': {
      const communication = getters.getCommunicationData(node);
      return communication ? { status: communication.status } : null;
    }
    default:
      return null;
  }
}

/**
 * Whether the user may attach/edit/remove files on the parent entity (not per-file privileges).
 * Matches API: parent privilege gate + FileMutationStatusPolicy on status.
 */
export function canMutateFilesOnParent(
  nodeType: NodeType,
  entity: FileParentEntityData | null | undefined,
  isAuthorized: IsAuthorizedFn
): boolean {
  if (!entity) {
    return false;
  }

  // RO nodes (package browse, snapshot copy, aggregate) never allow normal file attach/edit/delete.
  // Pending snapshot trim uses a separate path in FilesDetails.
  if (nodeIsReadOnlyForUi(nodeType)) {
    return false;
  }

  const parentKind = nodeTypeToFileParentKind(nodeType);
  if (parentKind != null && !parentStatusAllowsFileMutation(parentKind, entity.status)) {
    return false;
  }

  if (entity.status === StatusDto.Published) {
    return false;
  }

  if (nodeType === 'dataPackage' || nodeType === 'simulationDataset') {
    return entity.status === StatusDto.Pending && isAuthorized(PrivilegeEnum.DataPackage, 'update');
  }

  if (nodeType === 'sample') {
    if (entity.canUpdateSample !== undefined) {
      return entity.canUpdateSample;
    }
    return isAuthorized(PrivilegeEnum.Sample, 'update');
  }

  if (nodeType === 'shipping') {
    return isAuthorized(PrivilegeEnum.Shipping, 'update') || isAuthorized(PrivilegeEnum.Shipping, 'execute');
  }

  if (nodeType === 'communication') {
    return (
      isAuthorized(PrivilegeEnum.Communication, 'update') || isAuthorized(PrivilegeEnum.OwnCommunication, 'update')
    );
  }

  if (nodeType === 'sampleActivity' || nodeType === 'projectActivity' || nodeType === 'theoryActivity') {
    const privilege = getPrivilegeForEntity(nodeType, entity as never);
    return privilege != null && isAuthorized(privilege, 'update');
  }

  return false;
}
